// Starforge's typed view of the shared auth service.
//
// TRANSPORT LIVES IN /auth/auth-client.js.
// ----------------------------------------
// This file used to make its own fetch calls. So did Voidfront's sync.js and
// the drag racer's auth-sync.js -- three implementations of one contract that
// had already drifted apart. The shared client ships from the same folder as
// the endpoints it calls, so a change to a response shape and the code reading
// it are one deploy rather than three repos and a hope. It is loaded by a
// <script> tag in index.html; see src/drs-auth.d.ts for its types.
//
// What stays here is the part worth keeping: turning untrusted JSON and HTTP
// status codes into TYPED domain results the UI can switch on. The shared
// client deliberately stops at "here is the body and the status" -- deciding
// that a 403 means "check your inbox, not your password" is a product decision,
// and it belongs in the product.
//
// Single sign-on needs nothing from this file. The session cookie is set at the
// site root with Path=/, so a sign-in on the lab page or in any other game is
// already valid here. In dev, vite.config.ts's proxy (with cookieDomainRewrite)
// makes that true against localhost too.

export interface SessionStatus {
  loggedIn: boolean;
  username: string | null;
  /**
   * What to actually show on screen -- the nickname if there is one,
   * otherwise `username` (an opaque account key for a self-signup player
   * with no nickname yet, or an already-readable name for a legacy one).
   * Never used unformatted -- see formatPlayerName in ui/playerName.ts.
   */
  displayName: string | null;
}

export interface LoginResult {
  success: boolean;
  username?: string;
  error?: string;
  /**
   * The password was correct but the address is unconfirmed (16.90). Kept
   * separate from a plain failure because the remedy is different: the player
   * needs their inbox, not another guess at the password.
   */
  unverified?: boolean;
}

/** The game key this client signs up under; matches save.ashx's ?game=. */
const GAME = "starforge";

export async function checkSession(): Promise<SessionStatus> {
  // The shared client already fails safe to "not logged in" when the backend
  // is unreachable, rather than throwing -- which is what this used to do for
  // itself, and for the same reason: the login screen surfaces it to the
  // player, and a crashed app surfaces nothing. window.DrsAuth itself can
  // also just not exist (the /auth/auth-client.js <script> tag failing to
  // load at all, not merely its calls failing) -- same "not logged in"
  // fallback, since login is optional by design (Section 13).
  if (!window.DrsAuth) return { loggedIn: false, username: null, displayName: null };
  return window.DrsAuth.session();
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await window.DrsAuth.login(username, password);

  if (res.ok) {
    // The server returns the account key, which for an email signup is an
    // opaque 32-hex string rather than anything the player typed. Use it: it is
    // what the session and every save is keyed by.
    return { success: true, username: str(res.data.username) ?? username };
  }

  if (res.status === 401) {
    return { success: false, error: "Incorrect email or password." };
  }

  // 403 means the password was RIGHT but the address is unconfirmed (design
  // doc 16.90). Distinct from 401 on purpose, and the client must keep them
  // distinct too: "wrong password" and "check your inbox" send the player to
  // completely different places.
  if (res.status === 403 || res.reason === "unverified") {
    return {
      success: false,
      unverified: true,
      error: res.error ?? "Confirm your email address before signing in."
    };
  }

  if (res.status === 0) {
    return { success: false, error: "Couldn't reach the login server. Try again." };
  }

  return { success: false, error: describe(res) };
}

// ---------------------------------------------------------------------------
// Signup, verification and password reset (design doc 16.90)
//
// Every one of these is a POST, including the two that redeem a token from a
// mailed link. That is deliberate on the server side -- a mail scanner
// prefetching the link performs a GET against a PAGE and consumes nothing --
// and it is why these flows work by reading a token out of the URL and posting
// it, rather than the link hitting an endpoint directly.
//
// Since the shared account page exists at /auth/account.html, mailed links land
// THERE rather than here: with single sign-on a verification link confirms an
// account, not a Starforge membership, so every game would otherwise be
// building screens for a flow that is not its own. These stay because
// Starforge's own login screen still offers signup and reset inline, which is
// a better first-run experience than bouncing a new player to another page.
// ---------------------------------------------------------------------------

export interface AuthActionResult {
  success: boolean;
  error?: string;
  /** Set when a token was rejected as expired rather than simply wrong. */
  expired?: boolean;
  /**
   * Set when the signup guard refused the submission (auth A-16). The token is
   * spent either way, so the caller has to fetch a fresh one before the player
   * can try again.
   */
  formRefused?: boolean;
  /** Present on a successful verify/reset: the session is now established. */
  username?: string;
  email?: string;
}

/**
 * What the signup form has to carry back (auth A-16): the signed timing token
 * and the name the honeypot value must be sent under.
 */
export interface SignupFormToken {
  token: string;
  field: string;
}

/**
 * Fetch a signup form token, or null if the service could not supply one.
 *
 * Call this when the signup form is DISPLAYED, never when it is submitted. The
 * server measures the interval between the two and refuses anything faster than
 * a person could plausibly have filled the form in -- so a token fetched at
 * submit time is guaranteed to be refused, which presents as "signup is broken"
 * rather than as the guard doing its job.
 *
 * Null rather than throwing: the caller cannot do anything about it except tell
 * the player to try again, which is what it does.
 */
export async function fetchSignupToken(): Promise<SignupFormToken | null> {
  const res = await window.DrsAuth.formToken();
  if (!res.ok) return null;

  const token = str(res.data.token);
  const field = str(res.data.field);

  return token && field ? { token, field } : null;
}

export async function signup(
  email: string,
  password: string,
  guard: SignupFormToken,
  honeypot: string
): Promise<AuthActionResult> {
  return toActionResult(
    await window.DrsAuth.signup(email, password, GAME, undefined, {
      token: guard.token,
      field: guard.field,
      value: honeypot
    })
  );
}

export async function requestPasswordReset(email: string): Promise<AuthActionResult> {
  return toActionResult(await window.DrsAuth.forgotPassword(email, GAME));
}

export async function verifyEmail(token: string): Promise<AuthActionResult> {
  return toActionResult(await window.DrsAuth.verifyEmail(token));
}

export async function resetPassword(token: string, password: string): Promise<AuthActionResult> {
  return toActionResult(await window.DrsAuth.resetPassword(token, password));
}

export async function logout(): Promise<void> {
  // Best-effort -- the caller resets local state regardless, so a failure here
  // must not stop someone signing out of the UI in front of them.
  await window.DrsAuth.logout();
}

// ---------------------------------------------------------------------------

function toActionResult(res: {
  ok: boolean;
  status: number;
  data: Record<string, unknown>;
  reason: string | null;
  error: string | null;
  retryAfterSeconds: number | null;
}): AuthActionResult {
  if (res.ok) {
    return { success: true, username: str(res.data.username), email: str(res.data.email) };
  }

  return {
    success: false,
    expired: res.reason === "expired",
    formRefused: res.reason === "form",
    error: describe(res)
  };
}

/**
 * A message a player can act on.
 *
 * A 429 carries retryAfterSeconds, and saying how long beats "try later" --
 * without a number, someone retries immediately and extends their own lockout.
 */
function describe(res: {
  status: number;
  error: string | null;
  retryAfterSeconds: number | null;
}): string {
  if (res.status === 429 && res.retryAfterSeconds) {
    const secs = res.retryAfterSeconds;
    const wait = secs >= 60 ? `${Math.ceil(secs / 60)} minute(s)` : `${secs} seconds`;
    return `Too many attempts. Try again in ${wait}.`;
  }

  return res.error ?? `Request failed (${res.status}).`;
}

/**
 * A field from a parsed response, but only if it really is a string.
 *
 * The bodies come back as Record<string, unknown> because they are untrusted
 * input; narrowing here rather than casting means a malformed response degrades
 * to the caller's fallback message instead of putting "[object Object]" or
 * "undefined" in front of a player.
 */
function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}
